<template>
  <v-app id="inspire">
    <v-main>
      <v-container
        class="client-result"
        :class="{ 'client-result--login': mode === 'login' }"
        data-testid="client-result"
      >
        <v-row justify="center">
          <v-col cols="12" v-bind="columns">
            <locale-select v-if="mode !== 'login'" class="client-result__locale"></locale-select>
            <!-- U4: бренд проката первым — из ответа сервера, после входа. -->
            <header v-if="mode !== 'login' && company && company.name" class="client-result__brand" data-testid="client-result-brand">
              <span class="client-result__monogram" aria-hidden="true">{{ companyMonogram }}</span>
              <span class="client-result__brand-copy">
                <strong>{{ company.name }}</strong>
                <a v-if="company.phone" :href="phoneHref" class="client-result__link">{{ company.phone }}</a>
              </span>
            </header>

            <!-- REQ-13: live region в DOM с первого кадра — экранный диктор читает смену текста внутри него. -->
            <div role="status" aria-live="polite" data-testid="client-result-live">
              <!-- FM-04: пары во вкладке нет (другая вкладка, другое устройство) — вход, итог виден после него. -->
              <p v-if="mode === 'login'" class="client-result__note" data-testid="client-result-login-note">
                {{ $t("client_page.result.login_note") }}
              </p>
              <template v-else>
                <h1
                  ref="heading"
                  tabindex="-1"
                  class="text-h5 font-weight-bold client-result__heading"
                  :class="headingClass"
                  data-testid="client-result-heading"
                >
                  {{ heading }}
                </h1>
                <p v-for="(line, index) in hints" :key="index" class="client-result__hint" data-testid="client-result-hint">
                  {{ line }}
                </p>
              </template>
            </div>

            <p v-if="serverFailed" class="client-result__error" role="alert" data-testid="client-result-error">
              {{ $t("client_page.result.load_failed") }}
            </p>

            <!-- Операция, за которой следит страница: остаётся на месте, как бы ни менялся список в ответе. -->
            <div
              v-if="mode === 'track' && trackedRow"
              class="client-result__item client-result__item--static"
              data-testid="client-result-selected"
              :data-operation="trackedRow.id"
            >
              <span class="client-result__item-main">
                <span class="font-weight-medium">{{ trackedRow.kind }}</span>
                <span v-if="trackedRow.amount">{{ trackedRow.amount }}</span>
              </span>
              <span class="client-result__muted">{{ trackedRow.date }}</span>
            </div>

            <template v-if="mode === 'list'">
              <p v-if="!operations.length" class="client-result__hint" data-testid="client-result-empty">
                {{ $t("client_page.result.operations.empty") }}
              </p>
              <ul v-else class="client-result__list">
                <li v-for="item in visibleOperations" :key="item.key">
                  <button
                    type="button"
                    class="client-result__item"
                    data-testid="client-result-operation"
                    :data-operation="item.id"
                    @click="select(item)"
                  >
                    <span class="client-result__item-main">
                      <span class="font-weight-medium">{{ item.kind }}</span>
                      <span v-if="item.amount">{{ item.amount }}</span>
                    </span>
                    <span class="client-result__item-sub">
                      <span class="client-result__muted">{{ item.date }}</span>
                      <span :class="statusClass(item.state)">{{ item.status }}</span>
                    </span>
                  </button>
                </li>
              </ul>
              <v-btn
                v-if="hiddenCount > 0"
                outlined
                color="primary"
                min-height="44"
                class="mt-3"
                data-testid="client-result-show-earlier"
                @click="showEarlier = true"
              >{{ $t("client_page.result.operations.show_earlier") }}</v-btn>
            </template>

            <div v-if="actions" class="client-result__actions">
              <v-btn
                v-if="actions.retry"
                color="primary"
                depressed
                min-height="44"
                data-testid="client-result-retry"
                @click="toCabinet"
              >{{ $t("client_page.result.card.retry") }}</v-btn>
              <v-btn
                v-if="actions.reload"
                color="primary"
                depressed
                min-height="44"
                data-testid="client-result-reload"
                @click="reload"
              >{{ $t("client_page.result.reload") }}</v-btn>
              <v-btn
                v-if="actions.back"
                :color="'primary'"
                :depressed="!actions.retry"
                :outlined="!!actions.retry"
                min-height="44"
                data-testid="client-result-back"
                @click="toCabinet"
              >{{ $t("client_page.result.back") }}</v-btn>
              <v-btn
                v-if="actions.other"
                text
                color="primary"
                min-height="44"
                data-testid="client-result-other"
                @click="showOthers"
              >{{ $t("client_page.result.operations.other") }}</v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <client-login v-if="mode === 'login'" @logged-in="onLoggedIn"></client-login>
    </v-main>
  </v-app>
</template>

<script>
// FT-200 STEP-06 (REQ-11, CTR-03, FM-04, EC-06, SC-04): страница возврата из банка `/client_page/result`.
//
// Адрес — `?flow=payment|card&by=client|staff[&status=success|fail]`, пары в нём нет. Итог операции страница берёт
// только с сервера: входит парой вкладки (find_booking_for_client_v2) и ищет в ответе операцию вкладки по
// идентификатору — заказ Stripe или Yandex Pay по order_id, привязку карты по id платежа Т-Банка. `status` из адреса —
// подсказка банка и итогом не считается никогда: «успех» в адресе подделывается переходом по ссылке. Привязка — успех,
// только когда исход платежа привязки из группы ok и у клиента есть карта: карта, привязанная раньше, без этого платежа
// успехом не считается.
//
// Пока сервер не подтвердил — «Банк проверяет…» и опрос через 5, 10, 20, 30, 30, 30 с (не больше 6 повторов), дальше —
// «Результат появится в кабинете позже». Таймер снимается при уходе со страницы. Подписок cable нет (REQ-12).
//
// Пары во вкладке нет: by=client — форма входа, после входа «Ваши операции» (операции этого вида из ответа, новые
// сверху; за последние 24 часа — сразу, раньше — по «Показать ранее»; единственная за 24 часа выбирается сама);
// by=staff (привязка, начатая сотрудником) — нейтральный текст без итога.
//
// «Набор возврата» (implementation-plan → «Совместимость версий и откат»): страница и всё, что она импортирует,
// переживают откат vue к старому кабинету — банк хранит этот адрес у операций, начатых в новом кабинете. Поэтому здесь
// нет импортов ClientPage.vue, блоков кабинета, стора и роутера, а тексты — только client_page.result и
// client_page.login (resultIsolation.spec.js). Читаются ключи ответа, которые есть и в PR A, и в PR B.
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import clientCabinetSession from "@/utils/clientCabinetSession";
import { paymentGroup, cardGroup } from "@/utils/clientCabinetStatuses";
import { money, reportServerError, CARD_CHECK_WINDOW_MS } from "@/utils/clientCabinetCore";
import ClientLogin, { LOGIN_PATH } from "./ClientLogin";
import LocaleSelect from "@/components/common/LocaleSelect.vue";
import { telHref } from "@/lib/phone/display";

export const POLL_DELAYS_MS = [5000, 10000, 20000, 30000, 30000, 30000];
export const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;

const FLOWS = ["payment", "card"];
const PROVIDER_LISTS = { stripe: "stripe_payments", yandex_pay: "yandex_pay_payments" };

const PAYMENT_HEADINGS = {
  ok: "client_page.result.payment.ok",
  later: "client_page.result.payment.ok",
  fail: "client_page.result.payment.fail",
  wait: "client_page.result.payment.checking",
};
const CARD_HEADINGS = {
  ok: "client_page.result.card.ok",
  fail: "client_page.result.card.fail",
  wait: "client_page.result.card.checking",
};
const PAYMENT_ITEMS = {
  ok: "client_page.result.items.payment.ok",
  later: "client_page.result.items.payment.later",
  fail: "client_page.result.items.payment.fail",
  wait: "client_page.result.items.payment.wait",
};
const CARD_ITEMS = {
  ok: "client_page.result.items.card.ok",
  fail: "client_page.result.items.card.fail",
  wait: "client_page.result.items.card.wait",
};
const LATER_STATUSES = {
  refunded: "client_page.result.statuses.refunded",
  partially_refunded: "client_page.result.statuses.partially_refunded",
  disputed: "client_page.result.statuses.disputed",
  dispute_lost: "client_page.result.statuses.dispute_lost",
};
const KINDS = {
  stripe: "client_page.result.kinds.stripe",
  yandex_pay: "client_page.result.kinds.yandex_pay",
  card_binding: "client_page.result.kinds.card_binding",
};

function queryValue(value) {
  const single = Array.isArray(value) ? value[0] : value;
  return single === null || single === undefined ? "" : String(single).trim();
}

function timeOf(row) {
  const time = Date.parse(row && row.created_at);
  return Number.isNaN(time) ? null : time;
}

function sameId(a, b) {
  return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
}

export default {
  name: "ClientResult",
  components: { ClientLogin, LocaleSelect },
  data() {
    return {
      flow: null,
      by: "client",
      mode: "loading",
      pair: null,
      op: null,
      response: null,
      reading: false,
      attempts: 0,
      exhausted: false,
      timer: null,
      showEarlier: false,
      fromList: false,
      // Последнее чтение не удалось (сеть, 5xx, 429), а успешного ответа ещё не было — сбой нашего сервера, не банка.
      readFailed: false,
    };
  },
  computed: {
    columns() {
      return this.mode === "login" ? { sm: 8, md: 6, lg: 4, class: "pb-0" } : { sm: 10, md: 8, lg: 6 };
    },
    // Ни одного ответа сервера и последнее чтение — сбой: «банк проверяет» здесь было бы неправдой.
    serverFailed() {
      return this.mode === "error" || (this.mode === "track" && !this.response && this.readFailed);
    },
    company() {
      return (this.response && this.response.company) || null;
    },
    phoneHref() {
      const phone = (this.company && this.company.phone) || "";
      return telHref(phone);
    },
    companyMonogram() {
      const name = (this.company && this.company.name) || "RentProg";
      const words = name.trim().split(/\s+/).filter(Boolean);
      return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)).toLocaleUpperCase();
    },
    // Итог операции вкладки по ответу сервера: { state, row }. Нет ответа или строки — банк ещё проверяет.
    outcome() {
      return this.evaluate(this.op, this.response);
    },
    trackedRow() {
      return this.outcome.row ? this.describe(this.op.flow, this.outcome.row, this.op.provider) : null;
    },
    heading() {
      switch (this.mode) {
        case "track": {
          if (!this.response) return this.$t(this.readFailed ? "client_page.result.title" : "client_page.result.loading");
          if (this.outcome.state === "wait" && this.exhausted) return this.$t("client_page.result.later");
          const map = this.op.flow === "card" ? CARD_HEADINGS : PAYMENT_HEADINGS;
          return this.$t(map[this.outcome.state] || map.wait);
        }
        case "list":
          return this.$t("client_page.result.operations.title");
        case "staff":
          return this.$t("client_page.result.staff.title");
        case "loading":
          return this.$t("client_page.result.loading");
        default:
          return this.$t("client_page.result.title");
      }
    },
    headingClass() {
      if (this.mode !== "track") return null;
      if (this.outcome.state === "ok" || this.outcome.state === "later") return "client-result__ok";
      if (this.outcome.state === "fail") return "client-result__fail";
      return null;
    },
    hints() {
      switch (this.mode) {
        case "track": {
          const { state, row } = this.outcome;
          if (!this.response) return [];
          if (state === "wait") {
            return [this.$t(this.exhausted ? "client_page.result.later_hint" : "client_page.result.checking_hint")];
          }
          if (state === "later") {
            return [this.$t("client_page.result.current_status", { status: this.$t(LATER_STATUSES[row.status]) })];
          }
          if (state === "fail") {
            return [this.$t(this.op.flow === "card" ? "client_page.result.card.fail_hint" : "client_page.result.payment.fail_hint")];
          }
          return [];
        }
        case "list":
          if (!this.operations.length) return [];
          return [this.$t(this.flow === "card" ? "client_page.result.operations.hint_card" : "client_page.result.operations.hint_payment")];
        case "staff":
          return [this.$t("client_page.result.staff.close")];
        case "unknown":
          return [this.$t("client_page.result.unknown_flow")];
        default:
          return [];
      }
    },
    // Операции нужного вида из ответа, новые сверху; без отсечки по возрасту.
    operations() {
      const data = this.response || {};
      let rows = [];
      if (this.flow === "payment") {
        Object.keys(PROVIDER_LISTS).forEach((provider) => {
          (data[PROVIDER_LISTS[provider]] || []).forEach((row) => {
            if (row && row.order_id) rows.push(this.describe("payment", row, provider));
          });
        });
      } else if (this.flow === "card") {
        rows = (data.tinkoff_payments || [])
          .filter((row) => row && row.purpose === "card_binding" && row.id !== null && row.id !== undefined)
          .map((row) => this.describe("card", row));
      }
      return rows.sort((a, b) => (b.time || 0) - (a.time || 0));
    },
    recentOperations() {
      const now = Date.now();
      return this.operations.filter((item) => item.time !== null && now - item.time < RECENT_WINDOW_MS);
    },
    visibleOperations() {
      return this.showEarlier ? this.operations : this.recentOperations;
    },
    hiddenCount() {
      return this.operations.length - this.visibleOperations.length;
    },
    actions() {
      switch (this.mode) {
        case "track": {
          const state = this.outcome.state;
          return {
            back: true,
            retry: this.op.flow === "card" && state === "fail",
            reload: this.serverFailed && !this.timer,
            other: this.fromList,
          };
        }
        case "list":
          return { back: true };
        case "error":
          return { reload: true, back: true };
        case "unknown":
          return { back: true };
        default:
          return null;
      }
    },
  },
  created() {
    const query = (this.$route && this.$route.query) || {};
    const flow = queryValue(query.flow);
    this.flow = FLOWS.indexOf(flow) === -1 ? null : flow;
    this.by = queryValue(query.by) === "staff" ? "staff" : "client";
    if (!this.flow) {
      this.mode = "unknown";
      return;
    }
    const op = this.tabOperation();
    // Привязку начал сотрудник: операции этой вкладки нет, итог увидит прокат (REQ-11).
    if (this.by === "staff" && this.flow === "card" && !op) {
      this.mode = "staff";
      return;
    }
    const pair = clientCabinetSession.load();
    if (!pair) {
      this.mode = "login";
      return;
    }
    this.pair = pair;
    this.op = op;
    this.mode = op ? "track" : "loading";
    this.read();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.stopPolling();
  },
  methods: {
    // Операция вкладки того же сценария, что в адресе; операция другого сценария не подменяет выбранный адресом.
    tabOperation() {
      const op = clientCabinetSession.operation();
      return op && op.flow === this.flow ? op : null;
    },
    evaluate(op, data) {
      const none = { state: "wait", row: null };
      if (!op || !data) return none;
      if (op.flow === "payment") {
        const list = PROVIDER_LISTS[op.provider];
        const row = list && (data[list] || []).find((item) => item && sameId(item.order_id, op.orderId));
        return row ? { state: paymentGroup(row.status), row } : none;
      }
      const row = (data.tinkoff_payments || []).find((item) => item && sameId(item.id, op.paymentId));
      if (!row) return none;
      const state = cardGroup(row.outcome);
      // Свежая привязка: исход прошёл, но карты у клиента нет (сервер ещё не сохранил её) — не успех.
      if (state === "ok" && this.freshBinding(row) && !(data.client && data.client.tinkoff_card_bound)) {
        return { state: "wait", row };
      }
      return { state, row };
    },
    // Свежая привязка — в окне CARD_CHECK_WINDOW_MS (в нём же кабинет показывает «банк проверяет карту»). Старая привязка
    // из «Ваших операций» — итог по исходу: карту могли отвязать позже.
    freshBinding(row) {
      const time = timeOf(row);
      return time === null || Date.now() - time < CARD_CHECK_WINDOW_MS;
    },
    describe(flow, row, provider) {
      const time = timeOf(row);
      if (flow === "card") {
        const state = cardGroup(row.outcome);
        return {
          key: `card-${row.id}`, id: String(row.id), flow, paymentId: row.id, time, state,
          kind: this.$t(KINDS.card_binding),
          amount: "",
          date: this.formatDate(time),
          status: this.$t(CARD_ITEMS[state]),
        };
      }
      const state = paymentGroup(row.status);
      const status = state === "later"
        ? this.$t(PAYMENT_ITEMS.later, { status: this.$t(LATER_STATUSES[row.status]) })
        : this.$t(PAYMENT_ITEMS[state]);
      return {
        key: `${provider}-${row.order_id}`, id: String(row.order_id), flow, provider, orderId: row.order_id, time, state,
        kind: this.$t(KINDS[provider] || KINDS.stripe),
        amount: money(row.amount, this.company && this.company.currency_symbol),
        date: this.formatDate(time),
        status,
      };
    },
    formatDate(time) {
      if (time === null) return "";
      const options = { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" };
      try {
        return new Date(time).toLocaleString((this.$i18n && this.$i18n.locale) || undefined, options);
      } catch (e) {
        return new Date(time).toLocaleString();
      }
    },
    statusClass(state) {
      if (state === "ok" || state === "later") return "client-result__ok";
      if (state === "fail") return "client-result__fail";
      return "client-result__muted";
    },
    // Вход выполнил ClientLogin (пары во вкладке не было): ответ уже есть — список операций или операция вкладки.
    onLoggedIn({ bookingId, lastName, data }) {
      this.pair = { bookingId, lastName };
      this.response = data;
      this.op = this.tabOperation();
      if (this.op) this.track();
      else this.showOperations();
      this.focusHeading();
    },
    showOperations() {
      const recent = this.recentOperations;
      if (recent.length === 1) {
        this.select(recent[0]);
        return;
      }
      this.mode = "list";
    },
    // Выбранная операция становится операцией вкладки: дальше страница следит за ней по идентификатору.
    select(item) {
      // cardBefore операции из списка не пишется: её знает только блок карты, начавший привязку.
      const op = item.flow === "card"
        ? { flow: "card", paymentId: item.paymentId }
        : { flow: "payment", provider: item.provider, orderId: item.orderId };
      this.op = clientCabinetSession.startOperation(op) || op;
      this.fromList = true;
      this.track();
      this.focusHeading();
    },
    track() {
      this.stopPolling();
      this.mode = "track";
      this.attempts = 0;
      this.exhausted = false;
      this.scheduleIfWaiting();
    },
    showOthers() {
      this.stopPolling();
      this.mode = "list";
      this.focusHeading();
    },
    // REQ-13: после смены содержимого по действию клиента фокус — на заголовок, диктор читает новый итог.
    focusHeading() {
      this.$nextTick(() => {
        const heading = this.$refs.heading;
        if (heading && typeof heading.focus === "function") heading.focus();
      });
    },
    scheduleIfWaiting() {
      if (this.destroyed || this.mode !== "track" || this.timer) return;
      if (this.outcome.state !== "wait") return;
      if (this.attempts >= POLL_DELAYS_MS.length) {
        this.exhausted = true;
        return;
      }
      const delay = POLL_DELAYS_MS[this.attempts];
      this.attempts += 1;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.read();
      }, delay);
    },
    stopPolling() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    },
    reload() {
      if (this.mode === "track") {
        this.stopPolling();
        this.attempts = 0;
        this.exhausted = false;
      } else {
        this.mode = "loading";
      }
      return this.read();
    },
    // Ответ сервера по паре вкладки. Ошибку не печатаем в консоль: ошибка axios несёт пару в config.data.
    // Что делать с ответом, решает режим на момент ответа: в «track» — следить дальше, из «loading» — показать операции,
    // в «list» (клиент ушёл к списку, пока запрос был в полёте) — только обновить данные, режим не менять.
    read() {
      if (!this.pair || this.reading) return Promise.resolve();
      this.reading = true;
      return this.axios
        .post(LOGIN_PATH, { booking_id: this.pair.bookingId, last_name: this.pair.lastName })
        .then((response) => {
          if (this.destroyed) return;
          const data = (response && response.data) || {};
          if (data.error || !data.booking) {
            this.rejectPair();
            return;
          }
          this.response = data;
          this.readFailed = false;
          if (this.mode === "loading") this.showOperations();
        })
        .catch((error) => {
          if (this.destroyed) return;
          const status = error && error.response && error.response.status;
          if (status === 404 || status === 422) {
            this.rejectPair();
            return;
          }
          this.readFailed = true;
          // Сбой сервера — в отчёт один раз на страницу, не на каждый повтор опроса.
          if (status >= 500 && !this.reported) {
            this.reported = true;
            reportServerError(this, "ClientResult: find_booking_for_client_v2", error);
          }
          // Без ответа и без операции — строка ошибки и «Повторить»; при слежении сбой — попытка, опрос идёт дальше.
          if (this.mode === "loading") this.mode = "error";
        })
        .then(() => {
          this.reading = false;
          // Следующий повтор — после того, как запрос завершён: таймер, сработавший во время запроса, не теряется.
          if (!this.destroyed && this.mode === "track") this.scheduleIfWaiting();
        });
    },
    // Пару отверг сервер (бронирование удалено, фамилию поправили): пара и операция стираются, клиент входит заново.
    rejectPair() {
      this.stopPolling();
      clientCabinetSession.clear();
      this.pair = null;
      this.op = null;
      this.response = null;
      this.mode = "login";
    },
    toCabinet() {
      const navigation = this.$router.push({ path: "/client_page" });
      if (navigation && typeof navigation.catch === "function") navigation.catch(() => {});
    },
  },
};
</script>

<style scoped>
/* Заголовок получает фокус программно (после входа — для экранного диктора), он не интерактивный:
   кольцо фокуса на нём не нужно, иначе при загрузке страницы название обведено рамкой. */
.client-result__heading:focus {
  outline: none;
}
/* REQ-13: видимое кольцо фокуса — #1976d2 (4,6:1 на белом), в тёмной теме #64b5f6 (8:1). */
.client-result ::v-deep .v-btn:focus-visible,
.client-result a:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.theme--dark .client-result ::v-deep .v-btn:focus-visible,
.theme--dark .client-result a:focus-visible {
  outline-color: #64b5f6;
}

.client-result {
  --client-result-bg: #f5f5f5;
  --client-result-fg: rgba(0, 0, 0, 0.87);
  --client-result-muted: rgba(0, 0, 0, 0.6);
  --client-result-surface: #ffffff;
  --client-result-line: #e0e0e0;
  --client-result-accent: #1976d2;
  --client-result-accent-ink: #ffffff;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  padding-top: clamp(24px, 6vh, 64px);
  background: var(--client-result-bg);
  color: var(--client-result-fg);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

.theme--dark .client-result {
  --client-result-bg: #121212;
  --client-result-fg: rgba(255, 255, 255, 0.87);
  --client-result-muted: rgba(255, 255, 255, 0.6);
  --client-result-surface: #1e1e1e;
  --client-result-line: rgba(255, 255, 255, 0.12);
  --client-result-accent: #2196f3;
  --client-result-accent-ink: rgba(0, 0, 0, 0.87);
}

.client-result--login {
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.client-result__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
  padding: 20px 22px;
  border-radius: 20px;
  border: 1px solid var(--client-result-line);
  background: var(--client-result-surface);
  color: var(--client-result-fg);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12);
}

.client-result__locale {
  display: block;
  margin: 0 0 12px auto;
}

.client-result__monogram {
  display: grid;
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  place-items: center;
  border-radius: 50%;
  background: var(--client-result-accent);
  color: var(--client-result-accent-ink);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.client-result__brand-copy {
  display: block;
  min-width: 0;
}

.client-result__brand-copy strong {
  display: block;
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: clamp(18px, 5vw, 24px);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.03em;
  overflow-wrap: anywhere;
}

.client-result__heading {
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: clamp(22px, 5vw, 30px) !important;
  line-height: 1.3;
  word-break: break-word;
  margin-bottom: 8px;
}

.client-result__hint,
.client-result__note {
  font-size: 1rem;
  margin: 0 0 12px;
}

.client-result__link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--client-result-accent);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.client-result__list {
  list-style: none;
  padding-left: 0 !important;
  margin: 16px 0 0;
}

.client-result__list > li + li {
  margin-top: 8px;
}

.client-result__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-height: 44px;
  padding: 12px 16px;
  margin: 16px 0;
  text-align: left;
  border: 1px solid var(--client-result-line);
  border-radius: 12px;
  background: var(--client-result-surface);
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.client-result__list .client-result__item {
  margin: 0;
}

.client-result__item--static {
  cursor: default;
}

.client-result__item-main,
.client-result__item-sub {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  column-gap: 12px;
}

.client-result__item:focus-visible,
.client-result__link:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.client-result__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.client-result ::v-deep .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text) {
  background: var(--client-result-accent) !important;
  color: var(--client-result-accent-ink) !important;
}

.client-result ::v-deep .v-btn.v-btn--outlined,
.client-result ::v-deep .v-btn.v-btn--text {
  border-color: var(--client-result-accent);
  color: var(--client-result-accent) !important;
}

.client-result ::v-deep .v-btn {
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

/* REQ-13: контраст ≥ 4,5:1 на белом — #2e7d32 (5,1:1), #c62828 (5,6:1), вторичный rgba(0, 0, 0, 0.6) (5,7:1). */
.client-result__ok {
  color: #2e7d32;
}

.client-result__fail,
.client-result__error {
  color: #c62828;
}

.client-result__muted {
  color: var(--client-result-muted);
}

.theme--dark .client-result__ok {
  color: #81c784;
}

.theme--dark .client-result__fail,
.theme--dark .client-result__error {
  color: #ef9a9a;
}

.theme--dark .client-result__muted {
  color: var(--client-result-muted);
}

.theme--dark .client-result__item {
  border-color: rgba(255, 255, 255, 0.24);
}
</style>
